import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Lightbulb, Quote, Repeat2, Sparkles, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { financialImpulses, type ImpulseCategory } from "@/data/financialImpulses";
import { todayISO } from "@/lib/dates";

const todayIndex = Math.floor(Date.now() / 86_400_000) % financialImpulses.length;
const todayDate = todayISO();

const categoryConfig: Record<ImpulseCategory, { label: string; icon: React.ElementType; color: string; badge: string }> = {
  quote: {
    label: "Quote",
    icon: Quote,
    color: "text-primary-text",
    badge: "bg-primary/10 text-primary-text border-primary/20",
  },
  tip: {
    label: "Tip",
    icon: Lightbulb,
    color: "text-cyan",
    badge: "bg-cyan/10 text-cyan border-cyan/25",
  },
  habit: {
    label: "Habit",
    icon: Sparkles,
    color: "text-income-text",
    badge: "bg-income/10 text-income-text border-income/25",
  },
};

interface PersonalizedImpulse {
  id: string;
  content: string;
  category: string;
  author: string | null;
  source: string | null;
  goal_name: string | null;
}

export const DailyImpulse = () => {
  const [offset, setOffset] = useState(0);
  const [personalized, setPersonalized] = useState<PersonalizedImpulse | null>(null);
  const [personalizing, setPersonalizing] = useState(false);
  const [checked, setChecked] = useState(false);

  // On mount: check for today's personalized impulse
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { setChecked(true); return; }
      supabase
        .from("daily_impulses")
        .select("*")
        .eq("user_id", user.id)
        .eq("impulse_date", todayDate)
        .maybeSingle()
        .then(({ data }) => {
          if (data) setPersonalized(data as PersonalizedImpulse);
          setChecked(true);
        });
    });
  }, []);

  const handlePersonalize = async () => {
    setPersonalizing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const [txRes, budgetRes, goalRes] = await Promise.all([
        supabase.from("transactions").select("type, category, amount").eq("user_id", session.user.id).order("date", { ascending: false }).limit(30),
        supabase.from("budgets").select("category, amount").eq("user_id", session.user.id),
        supabase.from("financial_goals").select("name, current_amount, target_amount, target_date, motivation").eq("user_id", session.user.id),
      ]);

      const { data, error } = await supabase.functions.invoke("generate-daily-impulse", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          context: {
            transactions: txRes.data || [],
            budgets: budgetRes.data || [],
            goals: goalRes.data || [],
          },
        },
      });

      if (error) throw error;
      if (data?.impulse) setPersonalized(data.impulse as PersonalizedImpulse);
    } catch (err) {
      console.error("Personalization error:", err);
    } finally {
      setPersonalizing(false);
    }
  };

  const handleNext = () => setOffset((o) => (o + 1) % financialImpulses.length);

  // ── Personalized view ────────────────────────────────────────────────────
  if (personalized) {
    const cat = (personalized.category || 'tip') as ImpulseCategory;
    const cfg = categoryConfig[cat] || categoryConfig.tip;
    const Icon = cfg.icon;

    return (
      <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <CardHeader className="pb-2 pt-5">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon className={`h-4 w-4 ${cfg.color}`} />
              </div>
              Daily Impulse
            </CardTitle>
            <Badge variant="outline" className="text-xs bg-primary/10 text-primary-text border-primary/20 gap-1">
              <Sparkles className="h-3 w-3" />
              Personalized
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pb-5 space-y-2">
          <AnimatePresence mode="wait">
            <motion.div
              key={personalized.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <p className={`text-sm leading-relaxed ${cat === "quote" ? "italic" : ""}`}>
                {cat === "quote" && <span className="text-muted-foreground mr-1">"</span>}
                {personalized.content}
                {cat === "quote" && <span className="text-muted-foreground ml-1">"</span>}
              </p>
              {(personalized.author || personalized.source) && (
                <p className="text-xs text-muted-foreground mt-2">
                  {personalized.author && <span>— {personalized.author}</span>}
                  {personalized.source && <span className="italic">, {personalized.source}</span>}
                </p>
              )}
              {personalized.goal_name && (
                <p className="text-xs text-income-text mt-2">
                  🎯 For your goal: <span className="font-medium">{personalized.goal_name}</span>
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </CardContent>
      </Card>
    );
  }

  // ── Static curated view ──────────────────────────────────────────────────
  const index = (todayIndex + offset) % financialImpulses.length;
  const impulse = financialImpulses[index];
  const { label, icon: Icon, color, badge } = categoryConfig[impulse.category];

  return (
    <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <CardHeader className="pb-2 pt-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon className={`h-4 w-4 ${color}`} />
            </div>
            Daily Impulse
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-xs ${badge}`}>
              {label}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNext}
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Repeat2 className="h-3.5 w-3.5" />
              Next
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pb-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={impulse.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <p className={`text-sm leading-relaxed ${impulse.category === "quote" ? "italic" : ""}`}>
              {impulse.category === "quote" && <span className="text-muted-foreground mr-1">"</span>}
              {impulse.content}
              {impulse.category === "quote" && <span className="text-muted-foreground ml-1">"</span>}
            </p>
            {(impulse.author || impulse.source) && (
              <p className="text-xs text-muted-foreground mt-2">
                {impulse.author && <span>— {impulse.author}</span>}
                {impulse.source && <span className="italic">, {impulse.source}</span>}
              </p>
            )}
          </motion.div>
        </AnimatePresence>

        {checked && (
          <div className="mt-4 pt-3 border-t border-border/30">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePersonalize}
              disabled={personalizing}
              className="gap-2 text-xs border-primary/30 hover:border-primary/50 hover:bg-primary/5"
            >
              {personalizing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 text-primary-text" />
              )}
              {personalizing ? "Personalizing…" : "Personalize for me"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
